import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Phone, DollarSign, Plus } from 'lucide-react';
import { Input, Dropdown, PhoneInput } from '../forms';

interface AddWorkerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (workerData: any) => Promise<void>;
  isSubmitting?: boolean;
}

const AddWorkerModal: React.FC<AddWorkerModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [workerData, setWorkerData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    specialization: '',
    hourlyRate: '',
    emergencyContact: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const roleOptions = [
    { value: 'skilled', label: 'Skilled Worker' },
    { value: 'semi_skilled', label: 'Semi-Skilled Worker' },
    { value: 'unskilled', label: 'Unskilled Worker' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'foreman', label: 'Foreman' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'safety_officer', label: 'Safety Officer' },
    { value: 'operator', label: 'Equipment Operator' },
  ];

  const specializationOptions = [
    { value: 'electrician', label: 'Electrician' },
    { value: 'plumber', label: 'Plumber' },
    { value: 'carpenter', label: 'Carpenter' },
    { value: 'mason', label: 'Mason' },
    { value: 'painter', label: 'Painter' },
    { value: 'welder', label: 'Welder' },
    { value: 'hvac', label: 'HVAC Technician' },
    { value: 'general_labor', label: 'General Labor' },
  ];

  const handleInputChange = (field: string, value: string) => {
    setWorkerData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!workerData.name.trim()) {
      newErrors.name = 'Worker name is required';
    }

    if (!workerData.role) {
      newErrors.role = 'Worker role is required';
    }

    if (!workerData.specialization) {
      newErrors.specialization = 'Specialization is required';
    }

    if (workerData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workerData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (
      workerData.hourlyRate &&
      (isNaN(Number(workerData.hourlyRate)) || Number(workerData.hourlyRate) < 0)
    ) {
      newErrors.hourlyRate = 'Please enter a valid hourly rate';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...workerData,
        hourlyRate: workerData.hourlyRate ? Number(workerData.hourlyRate) : undefined,
      });

      // Reset form
      setWorkerData({
        name: '',
        email: '',
        phone: '',
        role: '',
        specialization: '',
        hourlyRate: '',
        emergencyContact: '',
        notes: '',
      });
      setErrors({});
    } catch {
      // Error is handled by parent component
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[480px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Plus className="h-5 w-5 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Add New Worker</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <User className="h-4 w-4 mr-2" />
                  Basic Information
                </h4>

                <div className="space-y-4">
                  <Input
                    label="Worker Name"
                    value={workerData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    placeholder="Enter worker's full name"
                    error={errors.name}
                    required
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Dropdown
                      label="Role"
                      placeholder="Select role"
                      options={roleOptions}
                      value={workerData.role}
                      onChange={value => handleInputChange('role', value)}
                      error={errors.role}
                      required
                    />

                    <Dropdown
                      label="Specialization"
                      placeholder="Select specialization"
                      options={specializationOptions}
                      value={workerData.specialization}
                      onChange={value => handleInputChange('specialization', value)}
                      error={errors.specialization}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <Phone className="h-4 w-4 mr-2" />
                  Contact Information
                </h4>

                <div className="space-y-4">
                  <Input
                    label="Email"
                    type="email"
                    value={workerData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    placeholder="worker@example.com"
                    error={errors.email}
                  />

                  <PhoneInput
                    label="Phone Number"
                    value={workerData.phone}
                    onChange={value => handleInputChange('phone', value)}
                  />

                  <Input
                    label="Emergency Contact"
                    value={workerData.emergencyContact}
                    onChange={e => handleInputChange('emergencyContact', e.target.value)}
                    placeholder="Emergency contact number"
                  />
                </div>
              </div>

              {/* Rate Information */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Rate Information
                </h4>

                <Input
                  label="Hourly Rate (Optional)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={workerData.hourlyRate}
                  onChange={e => handleInputChange('hourlyRate', e.target.value)}
                  placeholder="25.00"
                  error={errors.hourlyRate}
                />
              </div>

              {/* Additional Notes */}
              <div>
                <Input
                  label="Notes (Optional)"
                  value={workerData.notes}
                  onChange={e => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes about the worker"
                />
              </div>
            </form>
          </div>

          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Worker
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AddWorkerModal;
